// ═══════════════════════════════════════════════════════════════════════════
// Ver la app como otra persona del equipo
//
// Para depurar y para probar interfaces de roles que no existen en la casa.
//
// ── CÓMO SE VUELVE ─────────────────────────────────────────────────────────
//
// Entrar como otro REEMPLAZA la sesión: `verifyOtp` deja la del objetivo donde
// estaba la propia. Si no se guardara nada, volver a ser uno mismo obligaría a
// entrar de nuevo con contraseña — y entonces nadie usaría esto dos veces.
//
// Así que antes de cambiar se aparta la sesión propia y al volver se restaura
// con `setSession`. Va en `sessionStorage` y no en `localStorage` a propósito:
// muere al cerrar la pestaña, que es exactamente lo que se quiere de algo que
// solo sirve mientras dura la visita.
//
// No es un secreto nuevo: es la misma sesión que el navegador ya tenía
// guardada. Lo que sí sería nuevo —y por eso no se hace— es dejarla en un lugar
// que sobreviva al cierre.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "./sesion.js";
import { configSupabase } from "./config.js";

const GUARDADA = "vt-sesion-propia";
const QUIEN = "vt-viendo-como";

/** ¿Estoy viendo la app como alguien más? Devuelve a quién, o null. */
export function viendoComo() {
  try {
    const s = sessionStorage.getItem(QUIEN);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

/** ¿Hay una sesión propia apartada a la que volver? */
export const puedeVolver = () => {
  try { return !!sessionStorage.getItem(GUARDADA); } catch { return false; }
};

/**
 * Abre la app como otra persona del equipo.
 *
 * Quién puede hacerlo lo decide la base (`puede_suplantar`), no esta función:
 * solo el propietario, solo hacia un rol menor, solo dentro de su vinícola y
 * nunca desde una sesión que ya es suplantada. Los errores se dejan pasar tal
 * cual vienen — dicen exactamente cuál de esas reglas se topó.
 */
export async function entrarComo(tenantId, objetivo, etiqueta) {
  const { url, anonKey } = configSupabase();

  const { data: actual } = await supabase.auth.getSession();
  if (!actual?.session) throw new Error("No hay sesión que apartar.");

  const r = await fetch(`${url}/functions/v1/entrar-como`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${actual.session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ tenant_id: tenantId, objetivo }),
  });

  let cuerpo = null;
  try { cuerpo = await r.json(); } catch { /* respuesta sin JSON */ }
  if (!r.ok) throw new Error(cuerpo?.error || "No se pudo entrar como esa persona.");

  // Se aparta ANTES de cambiar: si se guardara después, un fallo a media
  // operación dejaría al dueño dentro de la otra sesión y sin regreso.
  try {
    sessionStorage.setItem(GUARDADA, JSON.stringify({
      access_token: actual.session.access_token,
      refresh_token: actual.session.refresh_token,
    }));
  } catch {
    throw new Error("Este navegador no deja apartar tu sesión, y sin eso no " +
                    "podrías volver a ser tú. No se hizo el cambio.");
  }

  const { error } = await supabase.auth.verifyOtp({
    token_hash: cuerpo.token_hash, type: "magiclink",
  });
  if (error) {
    try { sessionStorage.removeItem(GUARDADA); } catch { /* nada que limpiar */ }
    throw new Error(error.message);
  }

  // ── Canjear la marca por el renglón de ESTA sesión ───────────────────────
  //
  // Hasta aquí la sesión está abierta pero NO firmada: la marca vive en la
  // cuenta del objetivo y serviría para cualquier sesión suya, incluida la que
  // él abra mañana. Eso fue un defecto real —sus propios actos salían firmados
  // como del dueño— y se arregló atando la firma al `session_id`.
  //
  // Si el canje falla se DESHACE el cambio. Quedarse dentro de su cuenta sin
  // firma es exactamente lo que no puede pasar: sería operar como él sin
  // rastro de quién fue.
  const { error: errFirma } = await supabase.rpc("registrar_suplantacion");
  if (errFirma) {
    try { await volverASerYo(); } catch { /* se intenta; abajo se avisa igual */ }
    throw new Error(
      "Se abrió la sesión pero no se pudo firmar, así que se deshizo. " +
      "Sin firma, lo que hicieras quedaría a nombre de esa persona. (" +
      errFirma.message + ")");
  }

  try {
    sessionStorage.setItem(QUIEN, JSON.stringify({
      nombre: etiqueta || "otro usuario", role: cuerpo.role, hasta: cuerpo.hasta,
    }));
  } catch { /* sin almacenamiento: la barra no aparecerá, pero se entró */ }

  return { role: cuerpo.role, hasta: cuerpo.hasta };
}

/** Vuelve a la sesión propia. */
export async function volverASerYo() {
  let guardada = null;
  try { guardada = JSON.parse(sessionStorage.getItem(GUARDADA) || "null"); }
  catch { /* ilegible */ }
  if (!guardada?.refresh_token)
    throw new Error("No encontré tu sesión. Vuelve a entrar con tu cuenta.");

  // Se cierra el renglón ANTES de soltar la sesión: después ya no se tiene el
  // `session_id` con el que encontrarlo.
  try { await supabase.rpc("terminar_suplantacion"); }
  catch { /* si no se pudo, el vencimiento la cierra sola */ }

  const { error } = await supabase.auth.setSession(guardada);
  if (error) throw new Error(error.message);

  try {
    sessionStorage.removeItem(GUARDADA);
    sessionStorage.removeItem(QUIEN);
  } catch { /* nada que limpiar */ }
}

/** Se llama al cerrar sesión: no dejar rastros de una visita que ya terminó. */
export function olvidarSuplantacion() {
  try {
    sessionStorage.removeItem(GUARDADA);
    sessionStorage.removeItem(QUIEN);
  } catch { /* nada que limpiar */ }
}
